import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import { useSettingsCache, useAuthCache } from "@/shared/cache";
import { useTheme, type Theme } from "@/shared/hooks/useTheme";
import { usePalette, type Palette } from "@/shared/hooks/usePalette";
import { useContrast, type Contrast } from "@/shared/hooks/useContrast";
import { useTextSize } from "@/shared/hooks/useTextSize";
import { Input } from "@/v2/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/shared/components/ui/select";
import { Slider } from "@/shared/components/ui/slider";
import { Avatar, AvatarFallback } from "@/v2/components/ui/avatar";
import { Switch } from "@/v2/components/ui/switch";
import {
	useActivityOptions,
	useTaskOptions,
} from "@/v2/components/entry-form/useCodeOptions";
import { SettingRow } from "@/v2/components/settings/SettingRow";
import { SectionHeading } from "@/v2/components/settings/SectionHeading";
import { cn } from "@/v2/lib/utils";

/** Sentinel for the clearable option — Radix Select can't hold an empty value. */
const NO_DEFAULT = "__none__";
import { Sun, Moon, Monitor, ALargeSmall } from "lucide-react";

const themeOptions: { value: Theme; label: string; icon: typeof Sun }[] = [
	{ value: "light", label: "Light", icon: Sun },
	{ value: "dark", label: "Dark", icon: Moon },
	{ value: "system", label: "System", icon: Monitor },
];

function SegmentedThemeControl({
	value,
	onChange,
}: {
	value: Theme;
	onChange: (t: Theme) => void;
}) {
	return (
		<div className="inline-flex items-center rounded-lg border border-border bg-muted p-0.5">
			{themeOptions.map((opt) => (
				<button
					key={opt.value}
					onClick={() => onChange(opt.value)}
					className={cn(
						"inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
						value === opt.value
							? "bg-background text-foreground shadow-sm"
							: "text-muted-foreground hover:text-foreground",
					)}
				>
					<opt.icon className="size-4" />
					{opt.label}
				</button>
			))}
		</div>
	);
}

export default function Preferences() {
	useDocumentTitle("General");
	const { settings, updateSettings } = useSettingsCache();
	// No matterId: the firm's default-group codes — the set the settings endpoint
	// validates a default against.
	const {
		tasks,
		isLoading: tasksLoading,
		isError: tasksError,
	} = useTaskOptions("");
	const {
		activities,
		isLoading: activitiesLoading,
		isError: activitiesError,
	} = useActivityOptions("");
	// A failed fetch also yields an empty list, which would leave "None" as the
	// only choice — picking it would clear a default the user still has.
	const tasksUnavailable = tasksLoading || tasksError;
	const activitiesUnavailable = activitiesLoading || activitiesError;
	const hasNoDefaults =
		!settings.p_default_task_code && !settings.p_default_activity_code;
	const { auth } = useAuthCache();
	const { theme, setTheme } = useTheme();
	const { palette, setPalette } = usePalette();
	const { contrast, setContrast } = useContrast();
	const { textSize, setTextSize } = useTextSize();

	return (
		<div>
			<h2 className="text-lg font-semibold mb-4">General</h2>

			<SectionHeading>Profile</SectionHeading>
			<div className="flex flex-col sm:flex-row gap-4 px-2 py-4 border-b border-border/40">
				<div className="sm:w-1/3 flex flex-col gap-1.5">
					<label className="text-sm font-medium">Name</label>
					<div className="flex items-center gap-2">
						<Avatar className="h-8 w-8 shrink-0">
							<AvatarFallback className="bg-primary text-primary-foreground text-xs font-medium">
								{auth.initials}
							</AvatarFallback>
						</Avatar>
						<Input
							value={auth.displayName}
							disabled
							className="w-full opacity-70 cursor-not-allowed"
						/>
					</div>
				</div>
				<div className="sm:w-2/3 flex flex-col gap-1.5">
					<label className="text-sm font-medium">Email</label>
					<Input
						value={auth.email}
						disabled
						className="w-full opacity-70 cursor-not-allowed"
					/>
				</div>
			</div>
			<SettingRow
				title="Practice area"
				description="Your area of legal practice, used to tailor generated narratives"
				stacked
			>
				<Input
					className="max-w-xs"
					value={settings.practice_type}
					onChange={(e) =>
						updateSettings({ practice_type: e.target.value })
					}
				/>
			</SettingRow>
			<SettingRow
				title="Role"
				description="Your professional role (e.g. Associate, Partner)"
				stacked
			>
				<Input
					className="max-w-xs"
					value={settings.prof_type}
					onChange={(e) =>
						updateSettings({ prof_type: e.target.value })
					}
				/>
			</SettingRow>

			<SectionHeading>Billing</SectionHeading>
			<SettingRow
				title="Minimum billable increments"
				description="Smallest time increment used when rounding billable hours"
			>
				<Select
					value={settings.minimum_units ? "0.2" : "0.1"}
					onValueChange={(v) => updateSettings({ minimum_units: v === "0.2" })}
				>
					<SelectTrigger className="w-36">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="0.1">0.1 hours</SelectItem>
						<SelectItem value="0.2">0.2 hours</SelectItem>
					</SelectContent>
				</Select>
			</SettingRow>

			<SettingRow
				title="Default task code"
				description="Pre-fills new time entries when the matter allows it."
			>
				<Select
					value={settings.p_default_task_code ?? NO_DEFAULT}
					onValueChange={(v) => {
						const next = v === NO_DEFAULT ? null : v;
						updateSettings({
							p_default_task_code: next,
							// Clearing the last default hides the toggle below, so turn it
							// off in the same save rather than stranding it on with nothing
							// to apply — and unreachable.
							...(!next && !settings.p_default_activity_code
								? { p_hardcode_codes: false }
								: {}),
						});
					}}
					disabled={tasksUnavailable}
				>
					<SelectTrigger className="w-44">
						{/* Radix renders SelectValue's placeholder only for an empty value,
						    and ours is never empty (NO_DEFAULT at minimum), so these states
						    have to render in the trigger itself. */}
						{tasksError ? (
							<span className="text-muted-foreground">
								Couldn't load task codes
							</span>
						) : tasksLoading ? (
							<span className="text-muted-foreground">Loading…</span>
						) : (
							<SelectValue />
						)}
					</SelectTrigger>
					<SelectContent>
						<SelectItem value={NO_DEFAULT}>None</SelectItem>
						{tasks.map((t) => (
							<SelectItem key={t.taskCode} value={t.taskCode}>
								{t.task} — {t.taskDescription}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</SettingRow>

			<SettingRow
				title="Default activity code"
				description="Pre-fills new time entries when the matter allows it."
			>
				<Select
					value={settings.p_default_activity_code ?? NO_DEFAULT}
					onValueChange={(v) => {
						const next = v === NO_DEFAULT ? null : v;
						updateSettings({
							p_default_activity_code: next,
							...(!next && !settings.p_default_task_code
								? { p_hardcode_codes: false }
								: {}),
						});
					}}
					disabled={activitiesUnavailable}
				>
					<SelectTrigger className="w-44">
						{activitiesError ? (
							<span className="text-muted-foreground">
								Couldn't load activity codes
							</span>
						) : activitiesLoading ? (
							<span className="text-muted-foreground">Loading…</span>
						) : (
							<SelectValue />
						)}
					</SelectTrigger>
					<SelectContent>
						<SelectItem value={NO_DEFAULT}>None</SelectItem>
						{activities.map((a) => (
							<SelectItem key={a.activityCode} value={a.activityCode}>
								{a.activity} — {a.activityDescription}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</SettingRow>

			{/* Hidden rather than disabled when there is no default: a disabled
			    switch can't be turned back off. Still shown while the flag is on, so
			    an account already left in that state has a way to clear it. */}
			{(!hasNoDefaults || settings.p_hardcode_codes) && (
				<SettingRow
					title="Use these codes instead of predicting"
					description="Automatically tracked time gets these codes rather than predicted ones. Manual entries pre-fill either way."
				>
					<Switch
						checked={settings.p_hardcode_codes}
						onCheckedChange={(v) => updateSettings({ p_hardcode_codes: v })}
					/>
				</SettingRow>
			)}

			<SectionHeading>Appearance</SectionHeading>
			<SettingRow
				title="Color theme"
				description="Choose between light, dark, or system theme"
				stacked
			>
				<SegmentedThemeControl value={theme} onChange={setTheme} />
			</SettingRow>

			<div className="px-2 py-4 border-b border-border/40">
				<p className="text-sm font-medium mb-1">Style</p>
				<p className="text-sm text-muted-foreground mb-3">Choose a color palette</p>
				<div className="grid grid-cols-2 gap-4 max-w-[340px]">
					{([
						{
							value: "brand" as const,
							label: "Hourglass",
							accent: "var(--brand-900)",
							previewBg: "var(--surface-1)",
							previewSidebar: "var(--brand-900)",
							previewBorder: "var(--border)",
						},
						{
							value: "default" as const,
							label: "Standard",
							accent: "var(--neutral-600)",
							previewBg: "var(--surface-1)",
							previewSidebar: "var(--neutral-600)",
							previewBorder: "var(--border)",
						},
					]).map(({ value, label, accent, previewBg, previewSidebar, previewBorder }) => {
						const isActive = palette === value;
						return (
							<button
								key={value}
								type="button"
								onClick={() => setPalette(value as Palette)}
								className={cn(
									"group rounded-lg border-2 overflow-hidden transition-colors text-left",
									isActive
										? "border-primary ring-1 ring-primary/20"
										: "border-border hover:border-muted-foreground/40",
								)}
							>
								<div className="p-3" style={{ backgroundColor: previewBg }}>
									<div className="flex gap-1.5 h-[52px]">
										<div
											className="w-6 rounded flex flex-col gap-1 p-1"
											style={{ backgroundColor: previewSidebar }}
										>
											<div className="h-1 w-full rounded-full bg-white/30" />
											<div className="h-1 w-full rounded-full bg-white/30" />
										</div>
										<div className="flex-1 flex flex-col gap-1">
											<div
												className="h-2 w-1/3 rounded"
												style={{ backgroundColor: accent, opacity: 0.3 }}
											/>
											<div
												className="flex-1 rounded bg-white p-1.5 flex flex-col gap-1"
												style={{ border: `1px solid ${previewBorder}` }}
											>
												<div
													className="h-1 w-full rounded-full"
													style={{ backgroundColor: accent, opacity: 0.2 }}
												/>
												<div
													className="h-1 w-4/5 rounded-full"
													style={{ backgroundColor: accent, opacity: 0.15 }}
												/>
											</div>
										</div>
									</div>
								</div>
								<div
									className={cn(
										"flex items-center gap-2 px-3 py-2.5 border-t",
										isActive ? "border-primary/20 bg-primary/5" : "border-border",
									)}
								>
									<div className="h-3 w-3 rounded-full" style={{ backgroundColor: accent }} />
									<span
										className={cn(
											"text-sm font-medium",
											isActive ? "text-primary" : "text-muted-foreground",
										)}
									>
										{label}
									</span>
								</div>
							</button>
						);
					})}
				</div>
			</div>

			<div className="px-2 py-4 border-b border-border/40">
				<p className="text-sm font-medium mb-1">Contrast</p>
				<p className="text-sm text-muted-foreground mb-3">Adjust contrast level</p>
				<div className="grid grid-cols-2 gap-4 max-w-[340px]">
					{([
						{
							value: "regular" as const,
							label: "Regular",
							previewBg: "var(--surface-1)",
							previewFg: "var(--neutral-950)",
							previewBorder: "var(--border)",
						},
						{
							value: "high" as const,
							label: "High Contrast",
							previewBg: "#ffffff",
							previewFg: "#000000",
							previewBorder: "#000000",
						},
					]).map(({ value, label, previewBg, previewFg, previewBorder }) => {
						const isActive = contrast === value;
						return (
							<button
								key={value}
								type="button"
								onClick={() => setContrast(value as Contrast)}
								className={cn(
									"group rounded-lg border-2 overflow-hidden transition-colors text-left",
									isActive
										? "border-primary ring-1 ring-primary/20"
										: "border-border hover:border-muted-foreground/40",
								)}
							>
								<div className="p-3" style={{ backgroundColor: previewBg }}>
									<div className="flex flex-col gap-1.5 h-[40px] justify-center px-1">
										<div
											className="h-1.5 w-full rounded-full"
											style={{ backgroundColor: previewFg, opacity: 0.8 }}
										/>
										<div
											className="h-1.5 w-3/4 rounded-full"
											style={{ backgroundColor: previewFg, opacity: 0.5 }}
										/>
										<div className="h-px w-full" style={{ backgroundColor: previewBorder }} />
									</div>
								</div>
								<div
									className={cn(
										"flex items-center justify-center px-3 py-2.5 border-t",
										isActive ? "border-primary/20 bg-primary/5" : "border-border",
									)}
								>
									<span
										className={cn(
											"text-sm font-medium",
											isActive ? "text-primary" : "text-muted-foreground",
										)}
									>
										{label}
									</span>
								</div>
							</button>
						);
					})}
				</div>
			</div>

			<div className="px-2 py-4 border-b border-border/40">
				<p className="text-sm font-medium mb-1">Text size</p>
				<div className="flex items-center gap-3 mt-3">
					<ALargeSmall className="h-4 w-4 text-muted-foreground shrink-0" />
					<Slider
						value={[["small", "medium", "large"].indexOf(textSize)]}
						min={0}
						max={2}
						step={1}
						onValueChange={([v]) =>
							setTextSize((["small", "medium", "large"] as const)[v])
						}
						className="w-48"
					/>
					<ALargeSmall className="h-6 w-6 text-muted-foreground shrink-0" />
				</div>
				<div className="flex justify-between w-48 ml-7 mt-1.5">
					{(["Small", "Medium", "Large"] as const).map((label) => (
						<span key={label} className="text-[11px] text-muted-foreground">
							{label}
						</span>
					))}
				</div>
			</div>
		</div>
	);
}
